import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { paragraphsWithStyle, readDocument } from "@/office/word/document";
import { unsupported } from "@/core/check";
import { readFields } from "@/office/word/layout";

export const lektionBeschriftungen: Lesson = {
  id: "beschriftungen",
  title: "Beschriftungen und Abbildungsverzeichnis",
  summary: "Abbildungen und Tabellen durchnummerieren und automatisch auflisten.",
  durationMinutes: 9,
  learningGoals: [
    "Beschriftungen für Abbildungen und Tabellen einfügen",
    "Beschriftungskategorien unterscheiden",
    "Ein Abbildungsverzeichnis erzeugen",
  ],
  steps: [
    {
      id: "kategorien",
      title: "Beschriftungen mit Kategorien",
      explanation: [
        "Im Bilder-Modul hast du bereits eine Beschriftung eingefügt. Jetzt kommt der " +
          "Teil dazu, der sie im Aufbauteil wirklich nützlich macht: die Kategorie.",
        "Im Dialog „Beschriftung einfügen“ steht ein Auswahlfeld mit „Abbildung“, " +
          "„Tabelle“ und „Formel“. Word führt für jede Kategorie eine eigene Zählung – " +
          "Abbildungen laufen von 1 aufwärts, Tabellen unabhängig davon ebenfalls. Über " +
          "„Neue Bezeichnung“ lassen sich eigene Kategorien anlegen, etwa „Anlage“.",
        "Wichtig ist die Platzierung: Abbildungen werden üblicherweise unterhalb " +
          "beschriftet, Tabellen oberhalb. Word bietet beides im selben Dialog an.",
        "Über die Schaltfläche „Nummerierung“ lässt sich außerdem die Kapitelnummer " +
          "einbeziehen – aus „Abbildung 4“ wird dann „Abbildung 2-4“ für die vierte " +
          "Abbildung in Kapitel 2. Das funktioniert nur, wenn deine Überschriften wie im " +
          "letzten Modul nummeriert sind.",
      ],
      keyPoints: [
        "Verweise → Beschriftung einfügen, mit Kategorie.",
        "Jede Kategorie hat ihre eigene Zählung.",
        "Tabellen oberhalb, Abbildungen unterhalb beschriften.",
        "Kapitelnummer einbeziehen setzt nummerierte Überschriften voraus.",
      ],
      task: {
        instruction:
          "Füge deinem Dokument mindestens zwei Beschriftungen hinzu – zum Beispiel eine " +
          "für eine Abbildung und eine für eine Tabelle.",
        hint: "Verweise → Beschriftung einfügen, dann oben die Kategorie wählen.",
        check: async () => {
          const snapshot = await readDocument();
          const beschriftungen = paragraphsWithStyle(snapshot, "Caption");

          if (beschriftungen.length === 0) {
            return fail(
              "Es wurde noch keine Beschriftung gefunden.",
              "Eine getippte Zeile zählt nicht – die Beschriftung muss die Formatvorlage „Beschriftung“ tragen.",
            );
          }

          return fromCriteria(
            [
              criterion(
                `Mindestens zwei Beschriftungen (gefunden: ${beschriftungen.length})`,
                beschriftungen.length >= 2,
              ),
              criterion(
                "Alle Beschriftungen enthalten Text",
                beschriftungen.every((absatz) => absatz.text.length > 3),
              ),
            ],
            {
              success: "Die Beschriftungen sitzen – das Verzeichnis dazu ist jetzt ein Klick.",
              failure: "Da fehlt noch etwas.",
              hint: "Beide Beschriftungen müssen über den Dialog eingefügt werden, damit sie die richtige Formatvorlage bekommen.",
            },
          );
        },
      },
    },
    {
      id: "abbildungsverzeichnis",
      title: "Das Abbildungsverzeichnis",
      explanation: [
        "Sobald Beschriftungen vorhanden sind, erzeugt Word daraus ein Verzeichnis – " +
          "genau wie das Inhaltsverzeichnis aus den Überschriften.",
        "Der Weg: Cursor an die gewünschte Stelle, dann „Verweise“ → " +
          "„Abbildungsverzeichnis einfügen“. Im Dialog wählst du unter „Beschriftung“ " +
          "die Kategorie aus – für ein Tabellenverzeichnis also „Tabelle“ statt " +
          "„Abbildung“.",
        "Ein längerer Bericht hat damit oft drei Verzeichnisse: Inhalt, Abbildungen, " +
          "Tabellen. Alle drei aktualisierst du gemeinsam, indem du mit Strg + A das " +
          "gesamte Dokument markierst und F9 drückst.",
      ],
      keyPoints: [
        "Verweise → Abbildungsverzeichnis einfügen, Kategorie im Dialog wählen.",
        "Für Tabellen dasselbe mit der Kategorie „Tabelle“.",
        "Strg + A und F9 aktualisiert alle Verzeichnisse auf einmal.",
      ],
      task: {
        instruction:
          "Füge ein Abbildungsverzeichnis ein, das die Beschriftungen einer Kategorie " +
          "(etwa „Abbildung“) auflistet.",
        hint: "Cursor unter das Inhaltsverzeichnis, Verweise → Abbildungsverzeichnis einfügen → Kategorie wählen → OK.",
        check: async () => {
          const felder = await readFields();
          if (felder === null) {
            return unsupported(
              "Diese Word-Version stellt dem Add-in keine Felder zur Verfügung (ab WordApi 1.4).",
              "Mit Alt + F9 zeigt sich das Verzeichnis als { TOC \\h \\z \\c \"Abbildung\" }. Danach kannst du den Schritt selbst als erledigt markieren.",
            );
          }
          const verzeichnis = felder.filter((feld) => /^TOC\b/i.test(feld.code) && /\\c\s/i.test(feld.code));
          const beschriftungen = felder.filter((feld) => /^SEQ\b/i.test(feld.code));
          return fromCriteria(
            [
              criterion(`Beschriftungen vorhanden (gefunden: ${beschriftungen.length})`, beschriftungen.length > 0),
              criterion("Ein Abbildungsverzeichnis ist eingefügt", verzeichnis.length > 0),
            ],
            {
              success: "Das Verzeichnis listet die Abbildungen – F9 hält es aktuell.",
              failure: "Das Abbildungsverzeichnis fehlt noch.",
              hint: "Ein Inhaltsverzeichnis zählt nicht; es muss „Abbildungsverzeichnis einfügen“ sein.",
            },
          );
        },
      },
    },
  ],
};
